<template>
  <component
    :is="loading === 'lazy' ? IntersectionObserver : 'div'"
    :options="intersectionObserverOptions"
    class="teaser-group-load-more"
    @intersect="onIntersect"
  >
    <template v-if="teasers?.length">
      <SectionHeader
        v-if="props.title && maxItems !== 0 && teasers?.length"
        :label="props.title"
        :level="2"
      />

      <div
        ref="wrapper"
        class="teaser-grid"
        :class="{
          animated: initialized,
        }"
      >
        <ol ref="grid" class="grid" :class="{ 'is--narrow': narrow, 'is--list': isList }">
          <transition-group :name="initialized ? 'staggered' : ''">
            <li
              v-for="(teaser, index) in teasers"
              ref="items"
              :key="`teaser-grid-${teaser.id}`"
              class="grid-item"
              :style="{
                '--stagger-delay': `${!renderedTeasers.includes(teaser.id) ? (index - renderedTeasers.length) * staggerDelay : 0}ms`,
              }"
            >
              <component
                :is="getComponentEl(teaser)"
                v-bind="{ ...(teaser as ICollectionTeaser), ...imageLoadingProps(index) }"
                :size="index === 0 ? 'large' : 'regular'"
                aspect-ratio="4x3"
                :padded="props.component === 'poster' ? undefined : false"
                :show-date="showDate"
                :heading-size="props.component === 'poster' ? 'title-xs' : undefined"
                :renditions="computedRenditions"
                :show-intro="resolvedShowIntro"
                :heading-level="headingLevel || 2"
                :analytics="{ linkAnalyticsTag: 'overview' }"
                :show-label="showLabel"
                :fluid="fluid"
                :global-short="teaser.articleType === 'shorts' ? false : undefined"
                @click="
                  teaser.articleType === 'shorts'
                    ? openShortModal(shorts.findIndex((short) => short.id === teaser.id))
                    : undefined
                "
              />
            </li>
          </transition-group>
        </ol>
      </div>

      <Transition name="fade" mode="out-in">
        <div v-show="showLoadMore" class="load-more-btn-wrapper">
          <Button
            ref="button"
            label="Toon meer"
            aria-label="Toon meer items"
            type="cta"
            icon="eenvandaag-general:plus"
            icon-position="right"
            class="button"
            :class="{ 'is--loading': isLoading }"
            :analytics="{ linkAnalyticsTag: 'load-more' }"
            @click="onBtnClick"
          />
        </div>
      </Transition>
    </template>
    <div v-else-if="isLoading" class="loader">
      <Suspense>
        <Spinner />
      </Suspense>
    </div>

    <ShortsCarouselModal
      :visible="showModal"
      :active-item="activeShortItem"
      :items="shorts"
      @close-modal="toggleCarouselModal(false)"
    />
  </component>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, ref } from 'vue';
import type { ICollectionTeaser, ITeaser } from '~~/types/local_types';
import { useThrottleFn } from '@vueuse/core';
import useImagePriority from '~/composables/useImagePriority';
import {
  TeaserConditionalBlockListItem,
  TeaserListItem,
  TeaserPoster,
  IntersectionObserver,
} from '#components';
import useAdapter from '~/composables/useAdapter';
import ShortPosterListItem from '~/components/teaser/conditional/ShortPosterListItem.vue';

interface IProps {
  title?: string;
  filter?: object;
  initialItems?: ITeaser[]; // to use this array of items you must set loading: interaction.
  loading?: 'lazy' | 'eager' | 'interaction';
  priority?: 'high' | 'auto';
  teaserCount?: number;
  cacheKey?: string;
  narrow?: boolean;
  component?: string;
  collection?: 'articles' | 'dossiers';
  sort?: string;
  showDate?: boolean;
  offset?: number;
  showLabel?: boolean;
  headingLevel?: number;
  showIntro?: boolean;
  fluid?: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
  title: undefined,
  filter: undefined,
  initialItems: undefined,
  loading: 'eager',
  priority: 'auto',
  teaserCount: 12,
  cacheKey: '',
  narrow: false,
  component: 'block-list-item',
  collection: 'articles',
  sort: '-publish_time',
  showDate: true,
  offset: 0,
  showLabel: true,
  headingLevel: 2,
  showIntro: undefined,
  fluid: true,
});

const { getTeasers, getCount, getCachedTeasers } = useTeasers();

const instance = getCurrentInstance();
const combinedCacheKey = `related-${props.cacheKey ? props.cacheKey : instance?.uid}`;
const intersectionObserverOptions = {
  threshold: 0.01,
};

const page = useState(`${combinedCacheKey}-page`, () => 0);
const maxItems = ref(-1);
const teasers = ref<ITeaser[]>([]);
const { isUsingKeyboard } = useKeyboardNavigationDetection();

const button = ref<HTMLButtonElement | undefined>();
const showLoadMore = ref<boolean>(false);
const intersected = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const initialized = ref<boolean>(false);

const items = ref<HTMLElement[] | undefined>();
const grid = ref<HTMLElement | undefined>();
const wrapper = ref<HTMLElement | undefined>();
const showModal = ref(false);

const resolvedShowIntro = computed(() => {
  return props.showIntro ?? props.component === 'list';
});

const { dossierTeaserAdapter } = useAdapter();

const renderedTeasers = ref<string[]>([]);
const newTeasers = ref<string[]>([]);
const staggerDelay = 100;
const activeShortItem = ref();

const getComponentEl = (teaser: ITeaser) => {
  if (props.component === 'poster') return TeaserPoster;
  if (props.component === 'list') return TeaserListItem;
  return teaser.articleType !== 'shorts' ? TeaserConditionalBlockListItem : ShortPosterListItem;
};

const updateLoadMoreVisibility = (fetchedCount?: number) => {
  if (fetchedCount === 0) {
    showLoadMore.value = false;
    return;
  }

  // If we fetched less than requested, there can't be another page.
  if (typeof fetchedCount === 'number' && fetchedCount < props.teaserCount) {
    showLoadMore.value = false;
    return;
  }

  showLoadMore.value = maxItems.value > teasers.value.length;
};

const initialLoad = async () => {
  maxItems.value = await getCount((props.filter ?? {}) as never, props.collection);
  await loadTeasers(false, true);
};

const computedRenditions = computed(() => {
  // For posters only
  if (props.component !== 'poster') return;

  return [
    {
      breakpoint: 'sm',
      size: 480,
      aspectRatio: '16x9',
    },
    {
      breakpoint: 'md',
      size: 480,
      aspectRatio: '4x3',
    },
  ];
});

const loadTeasers = async (loadMore: boolean, getCached: boolean) => {
  const offset = page.value * props.teaserCount + (props.offset || 0);

  isLoading.value = true;
  let additionalTeasers = await getTeasers(
    props.teaserCount,
    props.filter ?? {},
    combinedCacheKey || '',
    props.sort,
    offset,
    loadMore,
    props.collection,
    getCached,
  );

  page.value += 1;

  // map dossiers to teasers
  if (props.collection === 'dossiers') {
    additionalTeasers = additionalTeasers?.map((teaser) => dossierTeaserAdapter(teaser));
  }

  const fetchedTeasers = additionalTeasers || [];
  teasers.value.push(...fetchedTeasers);

  updateLoadMoreVisibility(fetchedTeasers.length);

  isLoading.value = false;
};

const useCachedTeasers = () => {
  // check if we have cached teasers
  let cachedTeasers = getCachedTeasers(combinedCacheKey) || [];

  // map dossiers to teasers
  if (props.collection === 'dossiers') {
    cachedTeasers = cachedTeasers?.map((teaser) => dossierTeaserAdapter(teaser));
  }

  return cachedTeasers;
};

// show the initial items instanty (SSR)
if (props.initialItems && props.initialItems.length > 0) {
  intersected.value = true;

  teasers.value = [...props.initialItems, ...useCachedTeasers()];

  maxItems.value = await getCount((props.filter ?? {}) as never, props.collection);
  updateLoadMoreVisibility(props.initialItems.length);
}

const onBtnClick = () => {
  loadTeasers(true, false);
};

const onIntersect = async ({ visible }: never) => {
  if (!intersected.value && visible) {
    intersected.value = true;
    await initialLoad();
  }
};

onMounted(() => {
  // show items asap (after page load) when loading = eager.
  if (!props.initialItems && props.loading !== 'lazy') {
    intersected.value = true;
    initialLoad();
  }

  watch(
    () => items,
    () => {
      setDynamicHeight();
      splitNewArticles();
    },
    {
      immediate: true,
      deep: true,
    },
  );

  watch(
    isLoading,
    (newValue) => {
      if (button.value) {
        button.value.disabled = isLoading.value = newValue;
      }
    },
    {
      immediate: true,
    },
  );

  window.addEventListener('resize', throttledSetDynamicHeight);

  initialized.value = true;
});

onUnmounted(() => {
  window.removeEventListener('resize', throttledSetDynamicHeight);
});

const setDynamicHeight = () => {
  if (import.meta.client) {
    // 3 attempts to update the height should be enough.
    let count = 0;

    const run = () => {
      if (wrapper.value && grid.value && items.value && items.value.length > 0) {
        wrapper.value.style.height = grid.value.getBoundingClientRect().height + 'px';
      }

      count++;
      if (count === 3) {
        window.clearInterval(interval);
      }
    };

    const interval = window.setInterval(run.bind(this), 1000 / 30);
    run();
  }
};

const throttledSetDynamicHeight = useThrottleFn(setDynamicHeight, 1000 / 30, true, true);

const continueFocus = (index: number) => {
  const lastTeaser: HTMLElement | undefined = grid.value?.children[index] as
    | HTMLElement
    | undefined;

  if (lastTeaser) {
    const firstVisibleLink = Array.from(lastTeaser.querySelectorAll('a')).find((a) => {
      const style = window.getComputedStyle(a);
      return (
        a.offsetParent !== null && // not display: none or detached
        style.visibility !== 'hidden' &&
        style.display !== 'none' &&
        style.opacity !== '0'
      );
    });

    if (firstVisibleLink) {
      firstVisibleLink.focus();
      firstVisibleLink.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
};

const splitNewArticles = () => {
  let delayTime = 0;
  const lastOldTeaserIndex = renderedTeasers.value?.length - 1 || 0;

  // check which teasers are new (to be rendered)
  teasers.value?.forEach((teaser) => {
    if (!renderedTeasers.value.includes(teaser.id as string)) {
      newTeasers.value.push(teaser.id as string);
      delayTime += staggerDelay;
    }
  });

  // add a timeout to turn the new teasers into rendered teasers
  if (newTeasers.value?.length > 0) {
    const delay = delayTime - staggerDelay + 1000 / 30;

    // if we're using the keyboard to navigate we continue on the first loaded item
    if (isUsingKeyboard.value) {
      window.setTimeout(async () => {
        continueFocus(lastOldTeaserIndex + 1);
      }, delay / 3);
    }

    window.setTimeout(async () => {
      renderedTeasers.value.push(...newTeasers.value);
      newTeasers.value = [];
    }, delay);
  }
};

const imageLoadingProps = (index: number) => {
  return useImagePriority(props.priority, props.narrow ? 3 : 4, index);
};

const isList = computed(() => {
  return props.component === 'list';
});

const shorts = computed(() => {
  return teasers.value.filter((teaser) => teaser.articleType === 'shorts');
});

const openShortModal = (number: number) => {
  activeShortItem.value = number;
  toggleCarouselModal(true);
};

const toggleCarouselModal = (visible: boolean) => {
  showModal.value = visible;
};
</script>

<style scoped lang="postcss">
.teaser-grid {
  &.animated {
    overflow: hidden;
    transition: height 250ms ease;
  }
}

.grid {
  overflow: visible;
  height: auto;
  --grid-template-columns: repeat(2, 1fr);
  --grid-gap: var(--unit-16);

  display: grid;
  grid-template-columns: var(--grid-template-columns);
  align-items: stretch;
  gap: var(--grid-gap);

  @media (--md-viewport) {
    --grid-gap: var(--unit-24) var(--unit-16);
    --grid-template-columns: repeat(4, 1fr);

    &.is--narrow {
      --grid-template-columns: repeat(3, 1fr);
    }

    &.is--list {
      --grid-template-columns: 1fr;
    }
  }

  @media (--lg-viewport) {
    --grid-gap: var(--unit-24);
  }
}

.grid-item {
  position: relative;
  --grid-columns: span 2;
  grid-column: var(--grid-columns);
  @media (--md-viewport) {
    --grid-columns: span 1;
  }
}

.load-more-btn-wrapper {
  display: flex;
  justify-content: center;

  --padding: var(--unit-16);
  padding-top: var(--padding);

  @media (--lg-viewport) {
    --padding: var(--unit-20);
  }

  .button.is--loading {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.loader {
  padding: var(--unit-60);
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
}
</style>
